import { setDateWithTimezone } from '@systemeio/components'
import { AccessTypeCaptions } from 'modules/courses/courses/enums/access-type-enum'
import {
  DigitalResourcesTypeEnum,
  DigitalResourcesTypeTitles,
} from 'modules/digital-product/enums/digital-resources-type-enum'
import { resourceTypeIcons } from 'modules/digital-product/resources/constants/resource-type-icons'
import { useDigitalResources } from 'modules/digital-product/resources/hooks/use-digital-resources'
import { DigitalResourceEditType } from 'modules/digital-product/resources/types/digital-resource-edit-interface'
import { IconTile } from 'shared/components/icon-tile'
import React, { ReactNode } from 'react'
import { FieldErrorAndDescription } from 'shared/components'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { dateFormat } from 'shared/utils/date-time-format'
import { twMerge } from 'tailwind-merge'

interface ResourceRowProps {
  actions?: ReactNode
  className?: string
  error?: string
  resource: DigitalResourceEditType
}

/**
 * Read-only summary of a single resource: entity name plus a `type · access · dates` line.
 */
export function ResourceRow({ actions, className, error, resource }: ResourceRowProps) {
  const { t, tICU } = useLocoTranslation()
  const { user } = useUser()

  const data = useDigitalResources({
    resourceType: resource.type,
    courseId:
      resource.type === DigitalResourcesTypeEnum.membership_course ? resource.course : undefined,
    courseBundleId:
      resource.type === DigitalResourcesTypeEnum.membership_course_bundle
        ? resource.courseBundle
        : undefined,
  })

  const getName = () => {
    switch (resource.type) {
      // BE sends string
      case DigitalResourcesTypeEnum.membership_course:
        return data.coursesData?.find(el => el.id == resource.course)?.caption
      case DigitalResourcesTypeEnum.membership_course_bundle:
        return data.courseBundlesData?.find(el => el.id == resource.courseBundle)?.caption
      case DigitalResourcesTypeEnum.systemeio_plan:
        return data.plansData?.find(el => el.id == resource.value)?.caption
      case DigitalResourcesTypeEnum.systemeio_tag:
        return data.tagsData?.find(el => el.id == resource.value)?.caption
      case DigitalResourcesTypeEnum.community_access:
        return data.communitiesData?.find(el => el.id == resource.value)?.caption
      case DigitalResourcesTypeEnum.booking_event:
        return data.eventsData?.find(el => el.id == resource.value)?.caption
      default:
        return undefined
    }
  }

  const details: string[] = []

  if (resource.type) {
    details.push(t(DigitalResourcesTypeTitles[resource.type]))
  }

  if (
    resource.type === DigitalResourcesTypeEnum.membership_course ||
    resource.type === DigitalResourcesTypeEnum.membership_course_bundle
  ) {
    if (resource.enrollmentAccessType) {
      details.push(t(AccessTypeCaptions[resource.enrollmentAccessType]))
    }
  }

  if (resource.type === DigitalResourcesTypeEnum.membership_course) {
    if (resource.accessFrom) {
      details.push(
        t('dashboard.digital_product.resources.course.from', {
          // Read the date like the date picker does: the form keeps it as user-timezone wall
          // time without an offset, while BE returns it with one.
          date: dateFormat(setDateWithTimezone(resource.accessFrom, user?.timezone), {
            dateFormat: user?.dateFormat,
          }),
        }),
      )
    }
    if (resource.expirationPeriod != null) {
      details.push(
        tICU('dashboard.digital_product.resources.course.expires', {
          count: resource.expirationPeriod,
        }),
      )
    }
  }

  return (
    <div
      className={twMerge(
        'border-border-100 flex flex-col gap-1 rounded-lg border p-3',
        error && 'border-border-error',
        className,
      )}
    >
      <div className={'flex items-center justify-between gap-3'}>
        <div className={'flex min-w-0 items-center gap-2'}>
          <IconTile size={'s'} tone={'outline'}>
            {resource.type ? resourceTypeIcons[resource.type] : null}
          </IconTile>
          <div className={'min-w-0'}>
            <div className={'truncate text-base font-medium'}>{getName()}</div>
            <p className={'text-text-secondary truncate text-sm'}>{details.join(' · ')}</p>
          </div>
        </div>
        {actions}
      </div>
      <FieldErrorAndDescription error={error} />
    </div>
  )
}
